import { Layer } from 'grommet';
import { XIcon } from 'lucide-preact';
import { useEffect, useState } from 'preact/hooks';

import { ParsedUserProduct } from '@/queries/products/types';

import { Header, HeaderTitle, Section } from '../Section';
import FallbackProductDetails from './FallbackProductDetails';
import RecurringProductDetails from './RecurringProductDetails';
import SponsorshipProductDetails from './SponsorshipProductDetails';
import { getUserProductDetailView } from './utils';

interface UserProductDetailsModalProps {
  userProduct: ParsedUserProduct;
  activeUserProducts: ParsedUserProduct[];
  onClose: () => void;
}

const UserProductDetailsModal = ({ userProduct, activeUserProducts, onClose }: UserProductDetailsModalProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const detailView = getUserProductDetailView(userProduct);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsMobile(document.body.classList.contains('group/mobile-layout'));
        }
      });
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    setIsMobile(document.body.classList.contains('group/mobile-layout'));
    return () => observer.disconnect();
  }, []);

  if (detailView === 'unsupported') {
    return null;
  }

  return (
    <Layer
      key={isMobile ? 'mobile' : 'desktop'}
      full={isMobile ? 'horizontal' : undefined}
      animation={isMobile ? 'slide' : 'fadeIn'}
      position={isMobile ? 'bottom' : undefined}
      className="!overflow-auto !rounded-t-2xl mobile:!max-w-full desktop:!w-[calc(100%-3rem)] desktop:!max-w-[1098px] desktop:!rounded-b-2xl"
      onEsc={onClose}
      onClickOutside={onClose}
    >
      <Section>
        <Header className="mobile:px-3">
          <div className="flex items-center justify-between">
            <HeaderTitle>Purchase Details</HeaderTitle>
            <button
              type="button"
              className="preflight preflight-button flex items-center justify-center gap-3"
              onClick={onClose}
            >
              <XIcon className="size-5" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </Header>
        {detailView === 'recurring' ? (
          <RecurringProductDetails userProduct={userProduct} />
        ) : detailView === 'sponsorship' ? (
          <SponsorshipProductDetails userProduct={userProduct} activeUserProducts={activeUserProducts} />
        ) : (
          <FallbackProductDetails userProduct={userProduct} activeUserProducts={activeUserProducts} onClose={onClose} />
        )}
      </Section>
    </Layer>
  );
};

export default UserProductDetailsModal;
